import { useState, useEffect } from 'react'
import { ExternalServicesAPI, UsersAPI } from '../api'
import { Search, Edit, Trash2, Plus, ArrowUp, ArrowDown, ArrowUpDown, Globe, Users as UsersIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAdmin } from '../lib/hooks'
import { Pagination } from './Pagination'
import { ExternalServiceModal } from './ExternalServiceModal'
import { ProviderModal } from './ProviderModal'
import { Toggle } from './Toggle'

const AdminExternalServices = () => {
  const { isAdmin, loading: adminLoading } = useAdmin()
  const navigate = useNavigate()

  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [userFilter, setUserFilter] = useState<string>('ALL')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'createdAt', direction: 'desc' })

  const [editingService, setEditingService] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // For creating new service
  const [users, setUsers] = useState<any[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false)

  // Delete modal state
  const [deletingService, setDeletingService] = useState<any>(null)
  const [sendEmail, setSendEmail] = useState(true)

  // Transfer modal state
  const [transferringService, setTransferringService] = useState<any>(null)
  const [transferUserId, setTransferUserId] = useState<string>('')

  const load = async () => {
    setLoading(true)
    try {
      const [servicesRes, usersRes] = await Promise.all([
        ExternalServicesAPI.listAll(),
        UsersAPI.getAll()
      ])

      setItems(servicesRes.data || [])
      setUsers(usersRes.data || [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAdmin) load()
  }, [isAdmin])

  const handleDeleteClick = (service: any) => {
    setDeletingService(service)
    setSendEmail(true)
  }

  const handleTransferClick = (service: any) => {
    setTransferringService(service)
    setTransferUserId(service.userId)
  }

  const confirmDelete = async () => {
    if (!deletingService) return

    await ExternalServicesAPI.delete(deletingService.id)

    if (sendEmail && deletingService.user) {
      await ExternalServicesAPI.sendStatusEmail({
        userEmail: deletingService.user.email,
        userName: `${deletingService.user.firstName || ''} ${deletingService.user.lastName || ''}`.trim(),
        planName: deletingService.name,
        planPrice: String(deletingService.price),
        planCurrency: 'USD', // Asumimos USD ya que el componente muestra USD
        planPeriod: deletingService.payType,
        type: 'DELETED'
      })
    }

    setDeletingService(null)
    load()
  }

  const handleUpdate = async (data: any) => {
    if (!editingService) return
    const result = await ExternalServicesAPI.update(editingService.id, data)
    if (result.success) {
      setEditingService(null)
      load()
    } else {
      alert('Error al actualizar: ' + result.error)
    }
  }

  const handleCreate = async (data: any) => {
    if (!selectedUserId) {
      alert('Por favor selecciona un usuario')
      return
    }

    const user = users.find(u => u.id === selectedUserId)
    if (!user) return

    const result = await ExternalServicesAPI.create(user.clerkId, data)
    if (result.success) {
      setIsCreateModalOpen(false)
      setSelectedUserId('')
      load()
    } else {
      alert('Error al crear: ' + result.error)
    }
  }

  const filtered = items.filter((it) => {
    const q = query.trim().toLowerCase()
    const matchesQuery = q ? (
      it.name.toLowerCase().includes(q) ||
      it.domain?.toLowerCase().includes(q) ||
      (it.user?.email || '').toLowerCase().includes(q) ||
      (it.user?.firstName || '').toLowerCase().includes(q)
    ) : true

    const matchesUser = userFilter === 'ALL' ? true : it.userId === userFilter

    return matchesQuery && matchesUser
  })

  const sortedItems = [...filtered].sort((a, b) => {
    if (!sortConfig) return 0

    let aValue = a[sortConfig.key]
    let bValue = b[sortConfig.key]

    // Handle nested user properties
    if (sortConfig.key === 'user') {
      aValue = a.user?.email || ''
      bValue = b.user?.email || ''
    }

    if (typeof aValue === 'string') aValue = aValue.toLowerCase()
    if (typeof bValue === 'string') bValue = bValue.toLowerCase()

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
    return 0
  })

  const total = items.length
  const filteredTotal = sortedItems.length
  const startIdx = (page - 1) * pageSize
  const endIdx = Math.min(startIdx + pageSize, filteredTotal)
  const pageItems = sortedItems.slice(startIdx, endIdx)

  const onSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  if (adminLoading) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Cargando permisos...</p></div>
  if (!isAdmin) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Acceso restringido</p></div>

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-24 mb-16">
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl md:text-3xl font-bold">Servicios Externos</h2>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 shadow-sm hover:bg-gray-200 transition-colors dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700"
            >
              Volver
            </button>
            <button
              onClick={() => setIsProviderModalOpen(true)}
              className="px-4 py-2 rounded-md bg-white border border-gray-200 text-gray-700 shadow-sm hover:bg-gray-50 transition-colors dark:bg-slate-800 dark:border-slate-700 dark:text-gray-200 dark:hover:bg-slate-700 flex items-center gap-2"
            >
              <Plus size={18} />
              Nuevo Proveedor
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-4 py-2 rounded-md bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus size={18} />
              Nuevo Servicio
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1) }}
                placeholder="Buscar servicios, dominios, usuarios..."
                className="w-full border rounded-md px-3 py-2 pl-9 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder-gray-400"
              />
              <span className="absolute left-3 top-2.5 text-gray-400 text-sm">🔍</span>
            </div>
          </div>

          <select
            className="border rounded-md px-3 py-2 dark:bg-slate-800 dark:border-slate-700 dark:text-white max-w-[200px]"
            value={userFilter}
            onChange={(e) => { setUserFilter(e.target.value); setPage(1) }}
          >
            <option value="ALL">Todos los usuarios</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.email} ({u.firstName} {u.lastName})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden dark:bg-slate-800 dark:border-slate-700">
        {loading ? (
          <div className="px-5 py-8 text-gray-500">Cargando...</div>
        ) : (
          <div className="px-5 py-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-700/50">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 cursor-pointer select-none" onClick={() => onSort('name')}>
                    <div className="flex items-center gap-1">
                      Servicio
                      {sortConfig?.key === 'name' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                    </div>
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 cursor-pointer select-none" onClick={() => onSort('user')}>
                    <div className="flex items-center gap-1">
                      Usuario
                      {sortConfig?.key === 'user' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                    </div>
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3">Tipo</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 cursor-pointer select-none" onClick={() => onSort('price')}>
                    <div className="flex items-center gap-1">
                      Precio
                      {sortConfig?.key === 'price' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                    </div>
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3">Ciclo</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 cursor-pointer select-none" onClick={() => onSort('nextPaymentDate')}>
                    <div className="flex items-center gap-1">
                      Próximo Pago
                      {sortConfig?.key === 'nextPaymentDate' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                    </div>
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
                {pageItems.map((it) => (
                  <tr key={it.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center dark:bg-slate-700">
                          <Globe className="h-5 w-5 text-gray-500" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{it.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{it.domain || 'Sin dominio'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm text-gray-900 dark:text-white">{it.user?.email}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{it.user?.firstName} {it.user?.lastName}</div>
                    </td>
                    <td className="p-3 text-sm text-gray-600 dark:text-gray-300">
                      {it.type || '-'}
                    </td>
                    <td className="p-3 font-medium text-gray-900 dark:text-white">
                      USD {Number(it.price).toFixed(2)}
                    </td>
                    <td className="p-3 text-sm text-gray-600 dark:text-gray-300">
                      {it.payType}
                    </td>
                    <td className="p-3 text-sm text-gray-600 dark:text-gray-300">
                      {it.nextPaymentDate ? new Date(it.nextPaymentDate).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleTransferClick(it)}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-full transition-colors dark:text-amber-400 dark:hover:bg-amber-900/30"
                          title="Transferir a otro usuario"
                        >
                          <UsersIcon size={18} />
                        </button>
                        <button
                          onClick={() => setEditingService(it)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors dark:text-blue-400 dark:hover:bg-blue-900/30"
                          title="Editar"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(it)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors dark:text-red-400 dark:hover:bg-red-900/30"
                          title="Borrar"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {pageItems.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-gray-500">
                      No se encontraron servicios externos.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (
          <Pagination
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalItems={filteredTotal}
            className="bg-gray-50 dark:bg-slate-900/50 rounded-b-xl"
          />
        )}
      </div>

      {/* Delete Modal */}
      {deletingService && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md dark:bg-slate-800 dark:text-white dark:border dark:border-slate-700">
            <h3 className="text-lg font-semibold mb-2 dark:text-white">Eliminar Servicio Externo</h3>
            <p className="text-gray-600 mb-4 dark:text-gray-300">
              ¿Estás seguro de que deseas eliminar el servicio <strong>{deletingService.name}</strong> para {deletingService.user?.firstName}? Esta acción no se puede deshacer.
            </p>
            <div className="mb-6">
              <Toggle
                checked={sendEmail}
                onChange={setSendEmail}
                label="Enviar email de notificación al cliente"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeletingService(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md dark:text-gray-300 dark:hover:bg-slate-700"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-md"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {transferringService && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md dark:bg-slate-800 dark:text-white dark:border dark:border-slate-700">
            <h3 className="text-lg font-semibold mb-3 dark:text-white">Transferir Servicio Externo</h3>
            <p className="text-sm text-gray-600 mb-4 dark:text-gray-300">
              Estás por transferir <strong>{transferringService.name}</strong> del usuario actual
              {' '}
              <strong>{transferringService.user?.email}</strong> a otro usuario.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Nuevo propietario
              </label>
              <select
                value={transferUserId}
                onChange={(e) => setTransferUserId(e.target.value)}
                className="w-full border-2 rounded-md px-3 py-2 dark:bg-slate-700 dark:text-white transition-colors dark:border-slate-600"
              >
                {users
                  .slice()
                  .sort((a: any, b: any) => (a.firstName || '').localeCompare(b.firstName || ''))
                  .map((u: any) => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName} ({u.email})
                    </option>
                  ))}
              </select>
              {transferUserId && transferUserId !== transferringService.userId && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Este servicio aparecerá en el dashboard del nuevo usuario.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setTransferringService(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md dark:text-gray-300 dark:hover:bg-slate-700"
              >
                Cancelar
              </button>
              <button
                disabled={!transferUserId || transferUserId === transferringService.userId}
                onClick={async () => {
                  if (!transferUserId || transferUserId === transferringService.userId) return
                  await ExternalServicesAPI.update(transferringService.id, { userId: transferUserId })
                  setTransferringService(null)
                  load()
                }}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirmar transferencia
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal Wrapper */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          {/* We need to wrap the ExternalServiceModal to add User Selection first */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden p-6 relative">
            <h3 className="text-xl font-bold mb-4 dark:text-white">Seleccionar Usuario</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Usuario</label>
              <select
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
              >
                <option value="">Selecciona un usuario...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.email} ({u.firstName} {u.lastName})</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:border-slate-600 dark:hover:bg-slate-700"
              >
                Cancelar
              </button>
              <button
                disabled={!selectedUserId}
                onClick={() => setIsModalOpen(true)}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actual Create Modal */}
      {isModalOpen && isCreateModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
          <ExternalServiceModal
            isOpen={true}
            onClose={() => { setIsModalOpen(false); setIsCreateModalOpen(false); }}
            onConfirm={handleCreate}
          />
        </div>
      )}

      {/* Edit Modal */}
      {editingService && (
        <ExternalServiceModal
          isOpen={!!editingService}
          onClose={() => setEditingService(null)}
          onConfirm={handleUpdate}
          initialData={{
            name: editingService.name,
            type: editingService.type,
            domain: editingService.domain,
            price: editingService.price,
            payType: editingService.payType,
            startDate: editingService.startDate,
            nextPaymentDate: editingService.nextPaymentDate
          }}
        />
      )}

      <ProviderModal
        isOpen={isProviderModalOpen}
        onClose={() => setIsProviderModalOpen(false)}
        onSuccess={() => {
          // No es necesario recargar items ya que son servicios, 
          // pero el modal de servicios se alimentará de la nueva lista al abrirse.
        }}
      />
    </div>
  )
}

export default AdminExternalServices
